import axios from "axios";

import { getBaseUrl } from "./base-url";

/**
 * MOSA-467. The server-side wizard session.
 *
 * The wizard crosses five page loads: the paste, sign-up (email or Google), the
 * Google consent redirect, the Picker in a new tab, and a plain reload. Storage
 * covers some of those and not others, so the API holds the state and one id
 * goes in the URL, in `next=` and in the OAuth return path.
 *
 * These routes postdate `src/lib/generated/api-client.ts`, and regenerating
 * that needs a live API, so this module uses axios directly, the same way the
 * rest of the wizard does.
 */

export type SheetsWizardSession = {
  id: string;
  link: string | null;
  spreadsheetId: string | null;
  gid: number | null;
  step: number;
  googleConnectionId: string | null;
  pickedFileId: string | null;
  /** Datasource created for this run, when the API has recorded one. */
  datasourceId?: string | null;
  bound: boolean;
};

export type SheetsWizardSessionPatch = {
  link?: string;
  step?: number;
  googleConnectionId?: string;
  pickedFileId?: string;
  /**
   * Attach a created Sheets datasource to this session. The API returns 400
   * `wizard_session_datasource_spreadsheet_mismatch` when the datasource's
   * spreadsheet differs from the session link — update `link` first, or ask
   * the user to reconcile.
   */
  datasourceId?: string;
};

const authHeaders = (token: string | null) =>
  token ? { Authorization: `Bearer ${token}` } : {};

/** The session id grammar the API accepts: base64url, at most 128 chars. */
export const isSessionId = (value: string | null | undefined): boolean =>
  typeof value === "string"
    ? value.length > 0 && value.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value)
    : false;

export type CreateWizardSessionArgs = {
  link?: string;
  /** MOSA-908. Start from a Drive pick with no pasted link. */
  pickedFileId?: string;
};

/**
 * Start a wizard session. Pass a link string (legacy) or an object with a
 * link and/or a picked file id. An empty body is valid for the choice screen.
 */
export async function createWizardSession(
  token: string | null,
  linkOrArgs?: string | CreateWizardSessionArgs,
): Promise<SheetsWizardSession> {
  const body: CreateWizardSessionArgs =
    typeof linkOrArgs === "string"
      ? linkOrArgs
        ? { link: linkOrArgs }
        : {}
      : linkOrArgs ?? {};
  const payload: CreateWizardSessionArgs = {};
  if (body.link) payload.link = body.link;
  if (body.pickedFileId) payload.pickedFileId = body.pickedFileId;
  const response = await axios.post<SheetsWizardSession>(
    `${getBaseUrl()}/sheets/wizard-sessions`,
    payload,
    { headers: authHeaders(token) },
  );
  return response.data;
}

export async function readWizardSession(
  token: string | null,
  id: string,
): Promise<SheetsWizardSession> {
  const response = await axios.get<SheetsWizardSession>(
    `${getBaseUrl()}/sheets/wizard-sessions/${encodeURIComponent(id)}`,
    { headers: authHeaders(token) },
  );
  return response.data;
}

export async function patchWizardSession(
  token: string | null,
  id: string,
  patch: SheetsWizardSessionPatch,
): Promise<SheetsWizardSession> {
  const response = await axios.patch<SheetsWizardSession>(
    `${getBaseUrl()}/sheets/wizard-sessions/${encodeURIComponent(id)}`,
    patch,
    { headers: authHeaders(token) },
  );
  return response.data;
}

/**
 * Bind the session to the signed-in user. Called after sign-up and sign-in, so
 * the rest of the wizard run belongs to that user and nobody else.
 */
export async function bindWizardSession(
  token: string | null,
  id: string,
): Promise<SheetsWizardSession> {
  const response = await axios.post<SheetsWizardSession>(
    `${getBaseUrl()}/sheets/wizard-sessions/${encodeURIComponent(id)}/bind`,
    {},
    { headers: authHeaders(token) },
  );
  return response.data;
}
